<template>
  <div class="page-config-panel">
    <stat-panel
      v-for="(s, sIdx) in series.slice(0, this.viewport.cnt)"
      :key="sIdx"
      :config="config"
      :series="s"
      :palette="palette"
      :viewport="viewport"
    />
  </div>
</template>

<script>
import StatPanel from "@/components/StatPanel"

export default {
  components: {
    StatPanel
  },
  data: () => ({
    context: null,
    config: {
      title: '',
      operation: 'avg',
      reducer: 'avg',
      avg: Infinity,
      min: Infinity,
      max: Infinity,
      last: Infinity,
      sum: Infinity,
      unit: '',
      textColor: '#1C2B34FA'
    },
    series: [],
    metricsList: [
      'system.cpu.idle'
    ],
    tagList: [],
    palette: {
      background: 'red',
      rules: [
        {
          condition: '>= 0.66',
          background: 'green'
        },
        {
          condition: '>= 0.33',
          background: 'blue'
        }
      ]
    },
    activeMetric: 'system.cpu.user',
    activeHost: '*',
    activeGroup: '*',

    viewport: {
      width: 0,
      height: 0,
      colCnt: 1,
      rowCnt: 1,
      cnt: 0
    }
  }),
  mounted () {
    window.addEventListener('resize', this.handleResize)

    this.$client.getContext().then((c) => {
      this.context = c
      this.initPanel()
    })

    this.$client.events.on("dashboard_timeframe_change", (change) => {
      if (!this.context) { return }

      this.context.dashboard.timeframe = change
      this.initPanel()
    })
    this.$client.events.on("dashboard_custom_widget_options_change", (change) => {
      if (!this.context) { return }
      if (!this.context.dashboard) { return }

      this.context.widget.definition.options = change
      this.initPanel()
      this.loadMetrics()
    })
  },
  destroyed () {
    window.removeEventListener('resize', this.handleResize)
  },
  watch: {
    'series.length': {
      handler: 'handleResize'
    }
  },
  methods: {
    initPanel () {
      if (!this.context.widget) { return }
      const start = this.context.dashboard?.timeframe?.start || ''
      const end = this.context.dashboard?.timeframe?.end || ''

      const { options } = this.context.widget.definition

      this.activeMetric = options.metric
      this.activeHost = options.host || '*'
      this.activeGroup = options.group || '*'
      this.config.operation = options.operation || this.config.operation
      this.config.reducer = options.reducer || this.config.reducer
      this.config.textColor = options.textColor || '#1C2B34FA'

      this.palette.background = options.background
      this.palette.rules = [
        {
          condition: options.condition1,
          value: options.value1,
          background: options.background1
        },
        {
          condition: options.condition2,
          value: options.value2,
          background: options.background2
        }
      ].filter(({ value }) => !isNaN(value))
      .sort((a, b) => {
        if (a.condition === b.condition ) {
          switch(a.condition) {
          case 'Greater':
            return b.value - a.value
          case 'Less':
            return a.value - b.value
          }
        }

        return 0
      })
      .map(({ condition, value, background }) => ({
        condition: `${condition === 'Greater' ? '>' : '<'} ${value}`,
        background
      }))

      this.updateChart(start, end)
    },
    async updateChart (start, end) {
      const d = await this.$client.api
        .get('/api/v1/query', { params: {
          from: (start / 1000).toFixed(),
          to: (end / 1000).toFixed(),
          query: `${this.config.operation}:${this.activeMetric}{${this.activeHost}} by {${this.activeGroup}}`
        } })

      this.series = d.series

      this.config = {
        reducer: this.config.reducer,
        textColor: this.config.textColor
      }
    },
    async loadMetrics () {
      const start = this.context.dashboard?.timeframe?.start || ''

      const data = await this.$client.api.get('/api/v1/metrics', {
        params: { from: (start / 1000).toFixed() }
      })

      if (data.metrics.length <= this.metricsList.length) { return }

      const metricSet = new Set(this.metricsList.concat(data.metrics))
      this.metricsList = Array.from(metricSet)
      this.updateWidgetOptions()
    },
    updateWidgetOptions () {
      const { options = {} } = this.context.widget?.definition || {}

      this.$client.dashboard.customWidget.updateOptions([
        {
          default: this.metric || 'system.cpu.user',
          required: true,
          enum: this.metricsList,
          type: 'string',
          name: 'metric',
          label: 'Metric'
        },
        {
          type: 'string',
          name: 'host',
          label: 'From',
          default: options.activeHost || '*',
          required: true
        },
        {
          type: 'string',
          name: "group",
          label: "Group by",
          default: options.group || '*',
          required: true
        },
        {
          default: options.operation || 'avg',
          required: true,
          enum: [
            'avg',
            'max',
            'min',
            'sum'
          ],
          type: 'string',
          name: 'operation',
          label: 'Operation'
        },
        {
          default: options.reducer || 'avg',
          required: true,
          enum: [
            'avg',
            'max',
            'min',
            'sum',
            'last'
          ],
          type: 'string',
          name: 'reducer',
          label: 'Reduce By'
        },
        // {
        //   default: options.textColor || '#1C2B34FA',f
        //   type: 'string',
        //   name: 'textColor',
        //   label: 'Default Text Color'
        // },
        {
          default: options.background || 'red',
          enum: [
            'red',
            'green',
            'blue'
          ],
          required: true,
          type: 'string',
          name: 'background',
          label: 'Default Background Color'
        },
        {
          default: options.condition1 || 'Greater',
          enum: [
            'Greater',
            'Less'
          ],
          type: 'string',
          name: 'condition1',
          label: 'If the value is'
        },
        {
          default: options.value1 || '0.66',
          type: 'string',
          name: 'value1',
          label: 'Than'
        },
        {
          default: options.background1 || 'green',
          enum: [
            'red',
            'green',
            'blue'
          ],
          type: 'string',
          name: 'background1',
          label: 'Show with'
        },
        {
          default: options.condition2 || 'Greater',
          enum: [
            'Greater',
            'Less'
          ],
          type: 'string',
          name: 'condition2',
          label: 'If the value is'
        },
        {
          default: options.value2 || '0.33',
          type: 'string',
          name: 'value2',
          label: 'Than'
        },
        {
          default: options.background2 || 'blue',
          enum: [
            'red',
            'green',
            'blue'
          ],
          type: 'string',
          name: 'background2',
          label: 'Show with'
        }
      ])
    },
    handleResize () {
      const width = document.body.clientWidth
      const height = document.body.clientHeight

      if (!this.series.length) {
        this.viewport = {
          width,
          height,
          colCnt: 0,
          rowCnt: 0,
          cnt: 0
        }
      } else {
        const avColCnt = Math.max(Math.floor(width / 120), 1)
        const avRowCnt = Math.max(Math.floor(height / 120), 1)

        const cnt = Math.max(Math.min(this.series.length, 10), 1)
        const colCnt = Math.min(avColCnt, 5)
        const rowCnt = Math.min(avRowCnt, 2, Math.ceil(cnt / colCnt))

        this.viewport = {
          width,
          height,
          colCnt,
          rowCnt,
          cnt
        }
      }
    }
  }
}
</script>

<style>
html {
  height: 100%;
}
body {
  max-height: calc(100% - 16px);
  height: calc(100% - 16px);
  overflow: hidden
}
#app {
  height: 100%;
  display: flex;
  flex-direction: row;
  align-items: stretch;
}
.page-config-panel {
  width: 100%;
  flex: 0 0 100%;
  display: flex;
  flex-wrap: wrap;
  flex-direction: row;
  align-items: flex-start;
}
</style>
